import config from '../config';

// Domini esterni per cui non inviare l'header di autenticazione
const EXTERNAL_DOMAINS = [
  'router.project-osrm.org',
  'api.openrouteservice.org'
];

class AuthService {
  constructor() {
    this.jwtToken = localStorage.getItem('jwt_token');
    this.isLoggingIn = false;
    this.loginPromise = null;
  }

  async getCsrfToken() {
    try {
      const csrfUrl = `${config.API_URL}/session/token`;
      const response = await fetch(csrfUrl, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Errore nel recupero del CSRF token: ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      throw error;
    }
  }

  async getJwtToken() {
    try {
      const jwtUrl = `${config.API_URL}/jwt/token`;
      
      // Creiamo il Basic Auth header
      const credentials = btoa(`${config.JWT_CONFIG.credentials.username}:${config.JWT_CONFIG.credentials.password}`);
      
      const response = await fetch(jwtUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'omit'
      });

      // Otteniamo prima il testo della risposta
      const responseText = await response.text();
      
      try {
        // Proviamo a parsare il JSON
        const data = JSON.parse(responseText);
        
        if (!response.ok) {
          throw new Error(data.error || `Errore recupero JWT: ${response.status}`);
        }
        
        if (data.token) {
          localStorage.setItem('jwt_token', data.token);
          this.jwtToken = data.token;
          return data.token;
        } else {
          throw new Error('Token non presente nella risposta');
        }
      } catch (jsonError) {
        // Se il parsing JSON fallisce, controlliamo se è una risposta HTML
        if (responseText.includes('<!DOCTYPE html>')) {
          throw new Error('Endpoint JWT non accessibile - Risposta non valida');
        }
        // Altrimenti rilanciamo l'errore originale
        throw new Error(`Risposta non valida: ${responseText}`);
      }
    } catch (error) {
      console.error('Errore durante il recupero del token:', error);
      throw error;
    }
  }

  async login() {
    if (this.loginPromise) {
      return this.loginPromise;
    }

    try {
      this.isLoggingIn = true;
      
      if (!config.JWT_CONFIG.credentials.username || !config.JWT_CONFIG.credentials.password) {
        throw new Error('Credenziali mancanti');
      }

      this.loginPromise = (async () => {
        try {
          await this.getJwtToken();
          return this.jwtToken;
        } finally {
          this.isLoggingIn = false;
          this.loginPromise = null;
        }
      })();

      return await this.loginPromise;
    } catch (error) {
      this.clearTokens();
      this.isLoggingIn = false;
      this.loginPromise = null;
      throw error;
    }
  }

  clearTokens() {
    localStorage.removeItem('jwt_token');
    this.jwtToken = null;
  }

  // Metodo principale per fare richieste autenticate
  async request(url, options = {}) {
    const isExternalDomain = EXTERNAL_DOMAINS.some(domain => url.includes(domain));
    
    if (!isExternalDomain && !this.jwtToken) {
      try {
        await this.login();
      } catch (error) {
        throw error;
      }
    }

    try {
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...options.headers
      };

      if (!isExternalDomain && this.jwtToken) {
        headers['Authorization'] = `Bearer ${this.jwtToken}`;
      }

      const requestOptions = {
        ...options,
        headers,
        credentials: 'omit'  // Non inviare cookie per le richieste API
      };

      const response = await fetch(url, requestOptions);

      if ((response.status === 401 || response.status === 403) && !isExternalDomain) {
        this.clearTokens();
        await this.login();
        
        headers['Authorization'] = `Bearer ${this.jwtToken}`;
        
        const retryResponse = await fetch(url, {
          ...requestOptions,
          headers,
          credentials: 'omit'
        });
        
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json();
          throw new Error(errorData.message || `HTTP error! status: ${retryResponse.status}`);
        }
        
        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  getToken() {
    return this.jwtToken;
  }

  // Metodi helper per i diversi tipi di richieste
  async get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  async post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }
}

const authService = new AuthService();
export default authService; 